import { useLingui, Trans } from '@lingui/react/macro';
import { Button, Input, Label } from '@utima/ui';
import { Plus, Trash2 } from 'lucide-react';

export interface HeaderRow {
  /** Stable client-side id so React keys survive reordering/removal. */
  id: string;
  key: string;
  value: string;
}

export interface HttpToolHeaderRowsProps {
  rows: HeaderRow[];
  onChange: (rows: HeaderRow[]) => void;
  /** Header names already stored (values hidden) — shown as a hint on edit. */
  existingKeys?: string[];
  removedKeys?: string[];
  onToggleRemoval?: (key: string) => void;
}

/**
 * Editor for static request headers (auth tokens, API keys). Values are
 * write-only: stored encrypted and never returned, so on edit only the existing
 * header names are shown as a hint — re-enter a value to change it.
 */
export function HttpToolHeaderRows({
  rows,
  onChange,
  existingKeys,
  removedKeys = [],
  onToggleRemoval,
}: HttpToolHeaderRowsProps) {
  const { t } = useLingui();

  const update = (index: number, patch: Partial<HeaderRow>) =>
    onChange(rows.map((r, i) => (i === index ? { ...r, ...patch } : r)));

  const remove = (index: number) =>
    onChange(rows.filter((_, i) => i !== index));

  const add = () =>
    onChange([...rows, { id: crypto.randomUUID(), key: '', value: '' }]);

  return (
    <div className='space-y-3'>
      {existingKeys && existingKeys.length > 0 && (
        <div className='space-y-2'>
          <p className='text-xs text-muted-fg'>
            <Trans>
              Stored values stay hidden. Enter a header below to replace its
              value; other headers are kept.
            </Trans>
          </p>
          {existingKeys.map(key => (
            <div key={key} className='flex items-center justify-between gap-2'>
              <span
                className={
                  removedKeys.includes(key)
                    ? 'text-muted-fg line-through'
                    : 'text-sm'
                }
              >
                {key}
              </span>
              <Button
                size='sm'
                variant='ghost'
                onClick={() => onToggleRemoval?.(key)}
              >
                {removedKeys.includes(key) ? (
                  <Trans>Keep header</Trans>
                ) : (
                  <Trans>Remove header</Trans>
                )}
              </Button>
            </div>
          ))}
        </div>
      )}

      {rows.map((row, index) => (
        <div key={row.id} className='flex items-end gap-2'>
          <div className='flex-1 space-y-1.5'>
            <Label>
              <Trans>Header</Trans>
            </Label>
            <Input
              value={row.key}
              onChange={e => update(index, { key: e.target.value })}
              placeholder={t`e.g. Authorization`}
            />
          </div>
          <div className='flex-1 space-y-1.5'>
            <Label>
              <Trans>Value</Trans>
            </Label>
            <Input
              type='password'
              value={row.value}
              onChange={e => update(index, { value: e.target.value })}
              placeholder={t`e.g. Bearer …`}
            />
          </div>
          <Button
            size='sm'
            variant='ghost'
            title={t`Remove`}
            onClick={() => remove(index)}
          >
            <Trash2 className='size-4 text-danger' />
          </Button>
        </div>
      ))}

      <Button size='sm' variant='secondary' onClick={add}>
        <Plus className='size-4' />
        <Trans>Add header</Trans>
      </Button>
    </div>
  );
}
